import React, { useCallback, useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import type { FormikHelpers } from 'formik';

import { Form, Formik } from 'formik';

import { twMerge } from 'tailwind-merge';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';
import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import Icon from 'components/icon/icon';

import FileInputField from 'form-elements/file-input-field';

import useGetFreelancerInsuranceDocument from 'state/remote/remote-freelancer/use-get-freelancer-insurance-document';
import { useUploadFreelancerInsuranceDocument } from 'state/remote/remote-freelancer/use-upload-freelancer-insurance-document';
import useDeleteFreelancerInsuranceDocument from 'state/remote/remote-freelancer/use-delete-freelancer-insurance-document';
import type { InsuranceDocumentUploadType } from 'state/remote/remote-freelancer/use-upload-freelancer-insurance-document';

import {
  STATIC_FILES_URL,
  SUPPORTED_DOC_TYPES,
} from 'constants/signup-flow-constants';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';

import InsuranceInstructions from '../components/InsuranceInstructions';

import freelancerRoutes from '../freelancer-routes';

import validationSchema from './schema';

import DeleteInsuranceDocumentDialog from './DeleteInsuranceDocumentDialog';

const globalLocalize = createLocale('global');
const localize = createLocale('freelancerInsuranceInformationPageLocales');

const InsuranceInformation: React.FC = () => {
  const {
    docType,
    error,
    isSuccess,
    initialValues,
    isLoading,
    resetInsuranceUploadHook,
    uploadFreelancerInsuranceDocument,
  } = useUploadFreelancerInsuranceDocument();

  const { fileName, document } = useGetFreelancerInsuranceDocument();

  const { resetDeletionStatus } = useDeleteFreelancerInsuranceDocument();

  const [searchParams, setSearchParams] = useSearchParams({});

  const closeDeleteDocumentDialog = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const videoUrl = `${STATIC_FILES_URL}videos/signupflow/h6.mp4`;
  const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h6.png`;

  const navigate = useNavigate();
  const goToPersonRegisterPage = useCallback(() => {
    navigate(freelancerRoutes.personRegister.path);
  }, [navigate]);

  const goToDiplomasPage = useCallback(() => {
    navigate(
      `${freelancerRoutes.diplomasInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);

  useEffect(() => {
    if (error) {
      showErrorToast({
        description: error.message,
        title: globalLocalize('notification'),
      });
      resetInsuranceUploadHook();
    }
    if (isSuccess) {
      showSuccessToast({
        description: localize('documentAdded'),
        title: globalLocalize('notification'),
      });
      resetInsuranceUploadHook();
    }
    // eslint-disable-next-line
  }, [error, isSuccess]);

  const handleSubmitDoc = useCallback(
    (
      values: typeof initialValues,
      formikHelpers: FormikHelpers<typeof initialValues>,
    ) => {
      if (docType) {
        const docToUpload: InsuranceDocumentUploadType = {
          dtype: docType,
        };
        if (values.file.type === 'application/pdf')
          docToUpload.pdf = values.file;
        else docToUpload.picture = values.file;
        try {
          uploadFreelancerInsuranceDocument(docToUpload);
        } catch (er) {
          // eslint-disable-next-line
          console.log('Something went wrong');
        }

        formikHelpers.setValues(initialValues);
        formikHelpers.setErrors({});
        formikHelpers.setTouched({}, false);
      } else
        showErrorToast({
          description: localize('uploadErrorMessage'),
          title: globalLocalize('notification'),
        });
    },
    [uploadFreelancerInsuranceDocument, docType, initialValues],
  );

  const handleDeleteDocument = useCallback(() => {
    setSearchParams({ confirmDeleteDiploma: 'true' });
  }, [setSearchParams]);

  const onDeleteError = useCallback(
    (err: Error) => {
      showErrorToast({
        description: err.message,
        title: globalLocalize('notification'),
      });
      closeDeleteDocumentDialog();
      resetDeletionStatus();
    },
    [closeDeleteDocumentDialog, resetDeletionStatus],
  );

  const onDeleteSuccess = useCallback(() => {
    showSuccessToast({
      description: localize('documentDeleted'),
      title: globalLocalize('notification'),
    });
    closeDeleteDocumentDialog();
    resetDeletionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDeleteDocumentDialog]);

  const shouldOpenDialog = !!(
    searchParams.get('confirmDeleteDiploma') &&
    searchParams.get('confirmDeleteDiploma') === 'true'
  );

  const docClassName = twMerge(
    'h-11 flex justify-between items-center',
    'text-gray600 px-3  shadow-6 bg-white',
    'mt-6 border border-gray100 rounded-lg',
    'whitespace-nowrap overflow-x-scroll',
  );

  return (
    <FreelancerSignUpPageContainer
      headerTitle={localize('title')}
      showHeader
      showGoBackButton
      onGoBackClick={goToPersonRegisterPage}
      dialogInstructions={{
        component: <InsuranceInstructions />,
        shouldShowDialogInstructions: !document,
      }}
      videoInstructions={{
        url: videoUrl,
        videoPreviewImageURL,
      }}
    >
      <Typography as="h3" variant="body-medium" className="mt-6">
        {localize('helperMessage')}
      </Typography>
      {document && (
        <div className={docClassName}>
          <Typography
            as="h3"
            variant="body-medium"
            className="flex justify-start items-center h-full my-0 overflow-x-scroll max-w-[90%] whitespace-nowrap"
          >
            {`${fileName} (${document.name})`}
          </Typography>
          <Icon
            icon="trash"
            size={18}
            onClick={handleDeleteDocument}
            className="cursor-pointer"
          />
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmitDoc}
      >
        {({ setFieldValue, values }) => (
          <Form className="w-full mb-16">
            <FileInputField
              resetAfterSubmit={isSuccess || !!error}
              disabled={isLoading}
              accept={SUPPORTED_DOC_TYPES.join(', ')}
              name="file"
              label={localize('uploadProof')}
              onResetFile={() => setFieldValue('file', null as unknown as File)}
              onChange={(event) => {
                setFieldValue('file', event.currentTarget?.files?.[0]);
              }}
            />
            {(values.file !== null || isLoading) && (
              <Button
                disabled={docType === null}
                loading={isLoading}
                label={localize('saveDocument')}
                variant="primary"
                className="flex justify-center items-center w-full h-10 gap-2 py-2 px-8 border border-gray100 mt-6"
                type="submit"
              />
            )}
          </Form>
        )}
      </Formik>
      <Button
        variant="primary"
        label={globalLocalize('nextStep')}
        onClick={goToDiplomasPage}
        className="flex justify-center items-center mt-6 w-full"
      />
      <DeleteInsuranceDocumentDialog
        onDeleteError={onDeleteError}
        onDeleteSuccess={onDeleteSuccess}
        isOpen={shouldOpenDialog}
        currentDocumentId={document?.id?.toString() || ''}
        documentName={document?.name || ''}
        onCloseClicked={closeDeleteDocumentDialog}
      />
    </FreelancerSignUpPageContainer>
  );
};

export default InsuranceInformation;
